/**
 * Hero-Skills (Masterprompt Abschnitte 46, 52, 57).
 *
 * Jeder Hero hat eine Passive, drei aktive Grundskills und ein Ultimate.
 *
 * VERBINDLICH sind Namen, Rollen und die beschriebene Wirkung.
 * Alle Zahlen sind TUNING_PLACEHOLDER.
 *
 * Wichtig zur Trefferermittlung (Abschnitt 46):
 * Die Formen hier sind rein mathematisch - Kreise, Linien und Cones auf der
 * flachen 2D-Simulationsebene. Es gibt keine 3D-Physik im Kernkampf.
 */

/** Wie ein Skill gezielt wird. */
export type SkillTargeting =
  /** Richtung vom Hero aus, trifft alles in einem Kreisausschnitt. */
  | 'cone'
  /** Richtung vom Hero aus, trifft alles entlang einer Linie. */
  | 'skillshot'
  /** Punkt auf dem Boden, trifft alles in einem Kreis darum. */
  | 'ground_aoe'
  /** Wirkt nur auf den Hero selbst, braucht kein Ziel. */
  | 'self';

/** Ein Effekt, den ein Skill auf getroffene Gegner legt. */
export interface SkillHitEffects {
  /** Dauer der Betaeubung in Sekunden. 0 = keine. */
  stunSeconds: number;
  /** Dauer der Verlangsamung in Sekunden. 0 = keine. */
  slowSeconds: number;
  /**
   * Restliche Bewegung waehrend der Verlangsamung, 0 bis 1.
   * 0.5 bedeutet: halbe Geschwindigkeit.
   */
  slowFactor: number;
  /** Wie weit ein Treffer den Gegner zurueckstoesst. 0 = gar nicht. */
  knockbackDistance: number;
}

/** Ein Effekt, den ein Skill auf den Hero selbst legt. */
export interface SkillSelfEffects {
  /** Dauer in Sekunden. 0 = kein Selbsteffekt. */
  durationSeconds: number;
  /** Zusaetzliche Schadensreduktion, 0 bis 1. */
  damageReduction: number;
  /** True, wenn der Hero in dieser Zeit nicht betaeubt oder verlangsamt werden kann. */
  ccImmune: boolean;
}

export interface SkillDefinition {
  id: string;
  heroId: string;
  name: string;
  /** 1 bis 3 sind die Grundskills, 4 ist das Ultimate. */
  slot: 1 | 2 | 3 | 4;
  targeting: SkillTargeting;

  // --- Kosten (TUNING_PLACEHOLDER) ---
  manaCost: number;
  cooldownSeconds: number;

  // --- Geometrie (TUNING_PLACEHOLDER) ---
  /** Maximale Zielentfernung. Weiter entfernte Eingaben werden gekuerzt. */
  castRange: number;
  /** Radius bei ground_aoe, halbe Breite bei skillshot. */
  radius: number;
  /** Oeffnungswinkel bei cone, in Grad. */
  coneAngleDegrees: number;

  // --- Wirkung (TUNING_PLACEHOLDER) ---
  damage: number;
  /** Strecke, die der Hero sich dabei selbst bewegt. 0 = kein Dash. */
  dashDistance: number;

  /**
   * Wann die Bewegung der Figur trifft, in Sekunden nach dem Wirken
   * (gemessen von der Grafiksitzung an den Modelldateien, 25.10.2026).
   *
   * NUR OPTIK - der Schaden faellt auf dem Server sofort. Gebraucht wird
   * der Wert fuer eine einzige Frage: wie lange das Wirken den Hero
   * festhaelt (siehe skillBindung in game-rules). Ihn bis zum ENDE der
   * Animation zu binden waere traege gewesen - Deadfall dauert 1,7
   * Sekunden -, ihn gar nicht zu binden sah aus, als rutsche er durch
   * seinen eigenen Zauber.
   *
   * Aendert sich eine Animation, aendert sich dieser Wert mit. Er steht
   * deshalb bei den Spieldaten und nicht im Code.
   */
  hitSeconds: number;
  hitEffects: SkillHitEffects;
  selfEffects: SkillSelfEffects;

  /**
   * Bleibender Bereich am Einschlagpunkt (Bramble Field des Arcanist).
   *
   * Er wirkt weiter, wenn der Skill laengst vorbei ist - deshalb gehoert er
   * zur Simulation und nicht zu den Treffereffekten.
   */
  groundEffect?: {
    seconds: number;
    /** Restliches Tempo der Gegner darin, 0 bis 1. */
    slowFactor: number;
    /** Zuschlag auf den Radius des Skills. */
    radiusBonus: number;
  };

  /**
   * Eine gelegte Falle (Snare Trap des Deadeye, 22.10.2026).
   *
   * Ist das gesetzt, passiert beim WIRKEN nichts weiter: Schaden,
   * Treffereffekte und das Bodenfeld kommen erst, wenn die Falle
   * zuschnappt - und zwar an ihrem Ort, nicht am Hero.
   *
   * Die Zeiten stehen hier und nicht im Code, damit die Tests sie
   * ableiten koennen (CLAUDE.md, Lehre 1). Beide TUNING_PLACEHOLDER.
   */
  trap?: {
    /**
     * Sekunden vom Legen bis scharf.
     *
     * Vorher loest sie nicht aus - auch dann nicht, wenn beim Legen schon
     * jemand darin steht. Ohne diese halbe Sekunde waere die Falle ein
     * Flaechenzauber mit anderem Namen.
     */
    armSeconds: number;
    /** Sekunden, die sie ungenutzt liegen bleibt. Danach verfaellt sie. */
    lifetimeSeconds: number;
    /**
     * So lange steht das Zuschnappen noch im Zustand (kind "trap_snap").
     *
     * Nur fuer die Anzeige: Der Client bekommt zwanzigmal je Sekunde einen
     * Stand, und ein Augenblick, der nur einen Takt dauert, kann ihm
     * entgehen. Eine halbe Sekunde kann er nicht uebersehen.
     */
    snapSeconds: number;
  };

  /**
   * Verzoegerung bis zum Einschlag, in Sekunden (Deadfall des Arcanist).
   *
   * Der Einschlag wird angekuendigt: Wer rechtzeitig weggeht, entkommt ihm.
   * Ohne Ankuendigung waere ein Flaechenzauber dieser Groesse unfair.
   */
  delaySeconds?: number;

  /**
   * Abschlag je weiterem Getroffenen auf einer Bahn (Piercing Shot des Deadeye).
   *
   * Der Erste bekommt den vollen Schaden, jeder dahinter weniger - sonst waere
   * ein Schuss durch eine ganze Reihe staerker als jeder Flaechenzauber.
   */
  pierceFalloff?: {
    /** Abzug je Position dahinter, 0 bis 1. */
    perTarget: number;
    /** So viel bleibt mindestens uebrig, 0 bis 1. */
    minimum: number;
  };

  /**
   * Ein Skill, den der Hero ueber mehrere Sekunden wirkt (Barrage des
   * Deadeye).
   *
   * Waehrend dieser Zeit steht er still: Das ist der Preis fuer den hohen
   * Schaden. Jeder Schuss trifft den ERSTEN Gegner auf der Bahn.
   */
  channel?: {
    /** Gesamtdauer in Sekunden. */
    seconds: number;
    /** So viele Schuesse fallen in dieser Zeit. */
    shots: number;
  };

  /**
   * Zuschlag gegen angeschlagene Ziele (Kill Shot des Deadeye).
   *
   * 1 bedeutet: bei einem Gegner kurz vor dem Tod trifft es doppelt so hart.
   * Bei vollem Leben gibt es keinen Zuschlag.
   */
  executeBonus?: number;

  /**
   * So lange steht die Rauchwolke der Smoke Bomb (Shade).
   *
   * Versteckt ist der Hero, solange er IN dieser Wolke steht - nicht laenger
   * (Produktentscheidung vom 25.09.2026, zweite Fassung). Darin darf er
   * angreifen und Skills wirken, ohne gesehen zu werden; wer heraustritt, ist
   * sofort wieder ein Ziel.
   *
   * Die erste Fassung versteckte ihn ueberall, solange die Zeit lief - dann
   * war die Wolke aber nur Deko, und der Skill war schlicht "unsichtbar
   * werden" (Rueckmeldung vom 25.09.2026).
   */
  hiddenSeconds?: number;

  /** Umkreis der Rauchwolke. Genau darin ist er versteckt. */
  smokeRadius?: number;

  /**
   * Ein Schattenbild, das eine Zeit lang mitkaempft (Shadow Rift des Shade).
   *
   * Es benutzt dieselbe Technik wie die gekauften Defender - nur kostet es
   * kein Gold und belastet das Defense Cap nicht.
   */
  shadow?: {
    seconds: number;
  };

  /** Kurze Erklaerung fuer die Oberflaeche. */
  description: string;
}

const KEINE_TREFFEREFFEKTE: SkillHitEffects = {
  stunSeconds: 0,
  slowSeconds: 0,
  slowFactor: 1,
  knockbackDistance: 0,
};

const KEINE_SELBSTEFFEKTE: SkillSelfEffects = {
  durationSeconds: 0,
  damageReduction: 0,
  ccImmune: false,
};

/**
 * Die Skills des Vanguard (Masterprompt Abschnitt 57).
 *
 * Rolle: Tank / Bruiser. Er haelt Wellen auf, unterbricht und kontrolliert -
 * er ist kein Schadensausteiler.
 */
export const VANGUARD_SKILLS: readonly SkillDefinition[] = [
  {
    id: 'shield_slam',
    heroId: 'vanguard',
    name: 'Shield Slam',
    slot: 1,
    targeting: 'cone',
    // Minus 15 Prozent (v0.48): 45 -> 38. Siehe resourceRegen in heroes.ts.
    manaCost: 46,
    cooldownSeconds: 7,
    castRange: 7,
    radius: 0,
    coneAngleDegrees: 100,
    damage: 135,
    dashDistance: 0,
    hitSeconds: 0.2,
    hitEffects: {
      stunSeconds: 0.8,
      slowSeconds: 0,
      slowFactor: 1,
      // Der Stoss ist kurz und dient dem Unterbrechen, nicht dem Wegschieben.
      knockbackDistance: 1.5,
    },
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'Damages every enemy in front of you, stuns them briefly and interrupts them.',
  },
  {
    id: 'shield_rush',
    heroId: 'vanguard',
    name: 'Shield Rush',
    slot: 2,
    targeting: 'skillshot',
    manaCost: 61,
    cooldownSeconds: 12,
    castRange: 14,
    // Halbe Breite der getroffenen Bahn.
    radius: 2.5,
    coneAngleDegrees: 0,
    damage: 105,
    dashDistance: 14,
    hitSeconds: 0.05,
    hitEffects: {
      stunSeconds: 0,
      slowSeconds: 1.5,
      slowFactor: 0.6,
      knockbackDistance: 2,
    },
    selfEffects: {
      durationSeconds: 3,
      damageReduction: 0.25,
      ccImmune: false,
    },
    description: 'You charge forward, shove enemies aside and stand firmer for a moment.',
  },
  {
    id: 'groundbreaker',
    heroId: 'vanguard',
    name: 'Groundbreaker',
    slot: 3,
    targeting: 'ground_aoe',
    manaCost: 72,
    cooldownSeconds: 14,
    castRange: 10,
    radius: 6,
    coneAngleDegrees: 0,
    damage: 180,
    dashDistance: 0,
    hitSeconds: 0.32,
    hitEffects: {
      stunSeconds: 0,
      slowSeconds: 2.5,
      slowFactor: 0.5,
      // Ausdruecklich KEIN Pull (Masterprompt Abschnitt 57).
      knockbackDistance: 0,
    },
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'Shakes the ground: damage and a heavy slow all around you.',
  },
  {
    id: 'last_bastion',
    heroId: 'vanguard',
    name: 'Last Bastion',
    slot: 4,
    targeting: 'self',
    manaCost: 102,
    cooldownSeconds: 60,
    castRange: 0,
    radius: 0,
    coneAngleDegrees: 0,
    damage: 0,
    dashDistance: 0,
    hitSeconds: 0.25,
    hitEffects: KEINE_TREFFEREFFEKTE,
    selfEffects: {
      durationSeconds: 7,
      // Bewusst KEINE Unverwundbarkeit (Masterprompt Abschnitt 57).
      damageReduction: 0.5,
      ccImmune: true,
    },
    description: 'You hold the line: far less damage taken, and nothing can control you.',
  },
];

/**
 * Die Skills des Arcanist (Masterprompt Abschnitt 57).
 *
 * Rolle: AoE Mage. Er raeumt Gruppen weg und haelt Wellen auf Abstand - dafuer
 * haelt er selbst wenig aus. Wirkungen als Produktentscheidung vom 24.09.2026,
 * Zahlen sind TUNING_PLACEHOLDER.
 *
 * WALD STATT EIS (Produktentscheidung vom 16.10.2026).
 *
 * Robin: "Die Arcanist ist mit ihren Faehigkeiten so ein bisschen wie eine
 * Eis-Zauberin. In meiner visuellen Darstellung ist sie aber eher Richtung
 * Erde/Wald." Geaendert wurden deshalb NUR die Namen und die
 * Beschreibungen - keine Wirkung, keine Zahl, keine Kennung. Wer
 * frost_field liest, findet Bramble Field; die Kennung bleibt, weil sie in
 * Bot-Profilen und im Matcharchiv steht und kein Spieler sie sieht.
 *
 * Wald und nicht Stein, weil Stein und Boden beim Vanguard schon zur Rolle
 * gehoeren (Groundbreaker, Cracked Ground, Bedrock) - zwei Steinhelden
 * waeren schwerer auseinanderzuhalten.
 */
export const ARCANIST_SKILLS: readonly SkillDefinition[] = [
  {
    id: 'arcane_strike',
    heroId: 'arcanist',
    name: 'Thornburst',
    slot: 1,
    targeting: 'ground_aoe',
    manaCost: 54,
    /*
     * 15 statt 5 Sekunden (01.10.2026). Er war der Grund, warum der Arcanist
     * nur 259 Schaden je Kill brauchte, wo der Deadeye 453 brauchte: Radius 5
     * alle 5 Sekunden trifft bei 7 Creeps je Welle fast immer mehrfach, und
     * 45 Energie von 520 bedeuteten praktisch keine Begrenzung.
     */
    cooldownSeconds: 15,
    castRange: 16,
    radius: 5,
    coneAngleDegrees: 0,
    // 85 statt 95 - der Wurf soll seltener kommen UND etwas weniger wiegen.
    damage: 170,
    dashDistance: 0,
    hitSeconds: 0.18,
    hitEffects: KEINE_TREFFEREFFEKTE,
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'Thorns burst from the ground and hit everything around the point.',
  },
  {
    id: 'frost_field',
    heroId: 'arcanist',
    name: 'Bramble Field',
    slot: 2,
    targeting: 'ground_aoe',
    manaCost: 72,
    cooldownSeconds: 11,
    castRange: 16,
    radius: 6,
    coneAngleDegrees: 0,
    damage: 50,
    dashDistance: 0,
    hitSeconds: 0.25,
    hitEffects: {
      stunSeconds: 0,
      slowSeconds: 1,
      slowFactor: 0.45,
      knockbackDistance: 0,
    },
    // Das Feld bleibt liegen - darin ist der eigentliche Wert des Skills.
    groundEffect: { seconds: 5, slowFactor: 0.45, radiusBonus: 1 },
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'Brambles take over the ground: anyone walking through is slowed hard.',
  },
  {
    id: 'shockwave',
    heroId: 'arcanist',
    name: 'Lashing Vines',
    slot: 3,
    targeting: 'cone',
    manaCost: 66,
    cooldownSeconds: 9,
    castRange: 9,
    radius: 0,
    coneAngleDegrees: 90,
    damage: 120,
    dashDistance: 0,
    hitSeconds: 0.22,
    hitEffects: {
      // Kurz betaeubt heisst hier vor allem: unterbrochen.
      stunSeconds: 0.5,
      slowSeconds: 0,
      slowFactor: 1,
      knockbackDistance: 5,
    },
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'Vines whip everything in front of you back and interrupt it.',
  },
  {
    id: 'meteor',
    heroId: 'arcanist',
    name: 'Deadfall',
    slot: 4,
    targeting: 'ground_aoe',
    manaCost: 144,
    cooldownSeconds: 60,
    castRange: 20,
    radius: 9,
    coneAngleDegrees: 0,
    damage: 420,
    dashDistance: 0,
    hitSeconds: 1.2,
    hitEffects: {
      stunSeconds: 0.6,
      slowSeconds: 2,
      slowFactor: 0.6,
      knockbackDistance: 0,
    },
    // Angekuendigt: Der Einschlag kommt erst nach gut einer Sekunde.
    delaySeconds: 1.2,
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'Brings down an old trunk. It lands a moment later - and everyone sees where.',
  },
];

/**
 * Passive des Arcanist: Sap Surge (Produktentscheidung vom 24.09.2026, am
 * 16.10.2026 von "Arcane Overflow" umbenannt - siehe oben).
 *
 * Jeder von einem Skill getroffene Gegner laedt die Anzeige. Ist sie voll,
 * kostet der naechste Skill nichts und schlaegt haerter ein.
 *
 * Damit wird belohnt, wofuer der Arcanist da ist: viele Gegner auf einmal zu
 * treffen. Der Gegensatz zum Vanguard, der fuers Dastehen belohnt wird.
 */
export const ARCANE_OVERFLOW = {
  /** So viele Treffer laden die Anzeige voll. TUNING_PLACEHOLDER. */
  hitsForFull: 12,
  /** Zuschlag auf den Schaden des freien Skills. */
  bonusDamage: 0.5,
} as const;

/**
 * Die Skills des Deadeye (Masterprompt Abschnitt 57).
 *
 * Rolle: Ranger / Single Target. Er holt EIN hartes Ziel von weit weg herunter -
 * einen Brute, einen Elite, den Anfuehrer einer Welle. Gruppen wegraeumen ist
 * die Aufgabe des Arcanist, standhalten die des Vanguard.
 *
 * Wirkungen als Produktentscheidung vom 25.09.2026, die Zahlen sind
 * TUNING_PLACEHOLDER.
 */
export const DEADEYE_SKILLS: readonly SkillDefinition[] = [
  {
    id: 'piercing_shot',
    heroId: 'deadeye',
    name: 'Piercing Shot',
    slot: 1,
    targeting: 'skillshot',
    manaCost: 48,
    cooldownSeconds: 6,
    // Sein Markenzeichen: Er trifft weiter, als die Welle sehen kann.
    castRange: 22,
    radius: 1.6,
    coneAngleDegrees: 0,
    damage: 121,
    dashDistance: 0,
    hitSeconds: 0.18,
    /*
     * Weniger Abfall nach hinten (Produktentscheidung vom 30.09.2026): vorher
     * 25 Prozent je Ziel mit 40 Prozent Minimum.
     *
     * Der Schuss heisst "durchschlagend", kam hinten aber kaum noch an. Seit
     * die Wellen sechs bis sieben Creeps tragen (v0.53) steht der Deadeye
     * genau vor der Aufgabe, fuer die er gedacht ist - und traf den fuenften
     * Creep mit 40 Prozent. Jetzt sind es 55.
     */
    pierceFalloff: { perTarget: 0.15, minimum: 0.55 },
    hitEffects: KEINE_TREFFEREFFEKTE,
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'A long shot that punches through everything in a line - hardest up front.',
  },
  {
    id: 'snare_trap',
    heroId: 'deadeye',
    name: 'Snare Trap',
    slot: 2,
    targeting: 'ground_aoe',
    manaCost: 60,
    cooldownSeconds: 13,
    castRange: 14,
    radius: 3.5,
    coneAngleDegrees: 0,
    damage: 52,
    dashDistance: 0,
    hitSeconds: 0.22,
    hitEffects: {
      stunSeconds: 0,
      // Festhalten heisst hier: Tempo auf null. Schlagen duerfen sie weiter.
      slowSeconds: 1.5,
      slowFactor: 0,
      knockbackDistance: 0,
    },
    // Die Stelle bleibt danach noch einen Moment zaeh.
    groundEffect: { seconds: 4, slowFactor: 0.5, radiusBonus: 0 },
    /*
     * Sie wird GELEGT und schnappt erst zu (Produktentscheidung vom
     * 22.10.2026).
     *
     * Bis dahin hielt sie beim Wirken sofort alles im Radius fest - eine
     * Falle, die man wirft. Jetzt liegt sie am Zielpunkt, wird nach einer
     * halben Sekunde scharf und wartet, bis ein Gegner hineinlaeuft.
     * Ausloesen kann jeder Gegner, auch ein Creep; danach passiert genau
     * das, was vorher beim Wirken passierte - nur dort und dann.
     *
     * Zehn Sekunden Liegezeit (einschliesslich der halben Sekunde bis
     * scharf): lang genug, um einen Weg zu verstellen, kurz genug, dass
     * nicht das halbe Feld vermint ist.
     */
    trap: { armSeconds: 0.5, lifetimeSeconds: 10, snapSeconds: 0.5 },
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'Places a trap. It arms, waits, and snaps shut on the first enemy to step in.',
  },
  {
    id: 'barrage',
    heroId: 'deadeye',
    name: 'Barrage',
    slot: 3,
    targeting: 'skillshot',
    manaCost: 78,
    cooldownSeconds: 14,
    castRange: 18,
    radius: 1.2,
    coneAngleDegrees: 0,
    // Schaden PRO SCHUSS - sechs Schuesse ergeben den vollen Wert.
    damage: 52,
    dashDistance: 0,
    hitSeconds: 0.04,
    channel: { seconds: 1.8, shots: 6 },
    hitEffects: KEINE_TREFFEREFFEKTE,
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'Six quick shots in one direction. You stand still while firing.',
  },
  {
    id: 'kill_shot',
    heroId: 'deadeye',
    name: 'Kill Shot',
    slot: 4,
    targeting: 'ground_aoe',
    manaCost: 132,
    cooldownSeconds: 60,
    // Weiter als jeder andere Skill im Spiel - dafuer sieht man ihn kommen.
    castRange: 30,
    radius: 2.5,
    coneAngleDegrees: 0,
    damage: 450,
    dashDistance: 0,
    hitSeconds: 1,
    delaySeconds: 1,
    // Je weniger Leben das Ziel noch hat, desto haerter trifft es.
    executeBonus: 1,
    hitEffects: KEINE_TREFFEREFFEKTE,
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'You aim briefly and fire. It hits wounded enemies hardest.',
  },
];

/**
 * Passive des Deadeye: Marked Prey (Produktentscheidung vom 25.09.2026).
 *
 * Wer auf DAS SELBE Ziel schiesst, trifft es mit jedem Schuss haerter. Ein
 * Zielwechsel faengt wieder bei null an.
 *
 * Damit wird belohnt, wofuer der Deadeye da ist: dranbleiben, bis das harte
 * Ziel faellt. Gezaehlt wird an seinen Schuessen - Flaechenschaden staut sich
 * NICHT auf, sonst waere die Passive bei einer Welle sofort voll.
 */
export const MARKED_PREY = {
  /** So weit staut es sich hoechstens auf. */
  maxStacks: 5,
  /** Zuschlag je Stufe auf den Schaden gegen dieses eine Ziel. */
  damagePerStack: 0.08,
} as const;

/**
 * Die Skills des Shade (Masterprompt Abschnitt 57).
 *
 * Rolle: Assassin / Mobility. Er ist der einzige Hero mit ENERGY: kleiner
 * Vorrat, der doppelt so schnell zurueckkommt. Deshalb sind seine Faehigkeiten
 * billig und kurz getaktet - er wirkt oft etwas Kleines statt selten etwas
 * Grosses.
 *
 * Wirkungen als Produktentscheidung vom 25.09.2026, Zahlen sind
 * TUNING_PLACEHOLDER.
 */
export const SHADE_SKILLS: readonly SkillDefinition[] = [
  {
    id: 'blade_whirl',
    heroId: 'shade',
    name: 'Blade Whirl',
    slot: 1,
    targeting: 'cone',
    // Teurer und seltener (v0.50): vorher 25 Energie auf 4 Sekunden. Siehe
    // BALANCE.md - er war die staerkste Faehigkeit im Spiel und dabei gratis.
    manaCost: 48,
    cooldownSeconds: 6,
    castRange: 6,
    radius: 0,
    // Ein voller Kreis um ihn herum - er braucht keine Richtung.
    coneAngleDegrees: 360,
    /*
     * Von 70 auf 85 (Produktentscheidung vom 30.09.2026). In v0.50 war die
     * Faehigkeit teurer und seltener gemacht worden, weil sie die staerkste im
     * Spiel war - das galt in einer anderen Welt, mit Wellen aus dreieinhalb
     * Creeps. Jetzt sind es sechs bis sieben, und ein voller Kreis um den
     * Helden trifft sie alle. Genau das gewinnt in diesem Spiel.
     */
    damage: 115,
    dashDistance: 0,
    hitSeconds: 0.4,
    hitEffects: KEINE_TREFFEREFFEKTE,
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'Hits everything around you. Cheap and quickly ready again.',
  },
  {
    id: 'shadow_step',
    heroId: 'shade',
    name: 'Shadow Step',
    slot: 2,
    targeting: 'skillshot',
    manaCost: 48,
    cooldownSeconds: 9,
    castRange: 16,
    radius: 2,
    coneAngleDegrees: 0,
    damage: 108,
    dashDistance: 16,
    hitSeconds: 0.05,
    hitEffects: {
      stunSeconds: 0,
      slowSeconds: 0.8,
      slowFactor: 0.6,
      knockbackDistance: 0,
    },
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'You leap across a stretch and hit everything on the way.',
  },
  {
    id: 'smoke_bomb',
    heroId: 'shade',
    name: 'Smoke Bomb',
    slot: 3,
    targeting: 'self',
    manaCost: 36,
    cooldownSeconds: 16,
    castRange: 0,
    radius: 0,
    coneAngleDegrees: 0,
    damage: 0,
    dashDistance: 0,
    hitSeconds: 0.2,
    /*
     * Von 3 auf 4 Sekunden (Produktentscheidung vom 30.09.2026). Der Shade hat
     * mit 550 die wenigsten Lebenspunkte und die schlechteste
     * Verteidigungsguete aller vier (57 von 100 Portalleben im Sieg). Mehr
     * Fluchtfenster statt mehr Leben: Das passt zu einem Helden, der von
     * Beweglichkeit lebt, statt ihn in einen halben Vanguard zu verwandeln.
     */
    hiddenSeconds: 4,
    smokeRadius: 8,
    hitEffects: KEINE_TREFFEREFFEKTE,
    selfEffects: KEINE_SELBSTEFFEKTE,
    description:
      'Throw a cloud of smoke. Enemies cannot see you inside it - and you can still attack and cast. Step out and you are visible again.',
  },
  {
    id: 'shadow_rift',
    heroId: 'shade',
    name: 'Shadow Rift',
    slot: 4,
    targeting: 'self',
    manaCost: 96,
    cooldownSeconds: 60,
    castRange: 0,
    radius: 0,
    coneAngleDegrees: 0,
    damage: 0,
    dashDistance: 0,
    hitSeconds: 0.3,
    shadow: { seconds: 8 },
    hitEffects: KEINE_TREFFEREFFEKTE,
    selfEffects: KEINE_SELBSTEFFEKTE,
    description: 'A shadow splits off and fights at your side for a while.',
  },
];

/**
 * Passive des Shade: Momentum (Produktentscheidung vom 25.09.2026).
 *
 * Jeder Gegner, den er toetet, gibt ihm Rebound: mehr Tempo und schnellere
 * Schlaege. Es staut sich auf und verfaellt wieder, wenn eine Weile nichts
 * mehr faellt.
 *
 * Damit wird belohnt, wofuer er da ist: sich durch eine Welle arbeiten, ohne
 * stehen zu bleiben. Der Gegenentwurf zum Vanguard, der fuers Dastehen
 * belohnt wird.
 *
 * Nicht zu verwechseln mit dem Capstone "Momentum" des Vanguard - gleicher
 * Name aus dem Masterprompt, andere Wirkung.
 */
export const MOMENTUM = {
  /** So weit staut es sich hoechstens auf. */
  maxStacks: 5,
  /** Zuschlag auf das Tempo je Stufe. */
  moveSpeedPerStack: 0.06,
  /** Zuschlag auf die Angriffsgeschwindigkeit je Stufe. */
  attackSpeedPerStack: 0.08,
  /** So lange haelt der Schwung nach dem letzten Kill. */
  secondsUntilFade: 4,
} as const;

export const SKILLS: readonly SkillDefinition[] = [
  ...VANGUARD_SKILLS,
  ...ARCANIST_SKILLS,
  ...DEADEYE_SKILLS,
  ...SHADE_SKILLS,
];

export const SKILLS_BY_ID: ReadonlyMap<string, SkillDefinition> = new Map(
  SKILLS.map((skill) => [skill.id, skill]),
);

export function getSkill(id: string): SkillDefinition | undefined {
  return SKILLS_BY_ID.get(id);
}

/** Alle Skills eines Heroes, sortiert nach Slot. */
export function getSkillsForHero(heroId: string): SkillDefinition[] {
  return SKILLS.filter((skill) => skill.heroId === heroId).sort((a, b) => a.slot - b.slot);
}

/** Der Skill eines Heroes in einem bestimmten Slot. */
/**
 * Kann man diesen Hero schon waehlen?
 *
 * Es gibt vier Heroes in den Daten, aber ausgebaut ist immer nur der, dessen
 * Meilenstein fertig ist. Woran man das erkennt: Er hat Skills. So kann hier
 * nie jemand auswaehlbar werden, der im Match nichts kann.
 */
export function isPlayableHero(heroId: string): boolean {
  return getSkillsForHero(heroId).length > 0;
}

export function getSkillInSlot(heroId: string, slot: number): SkillDefinition | undefined {
  return SKILLS.find((skill) => skill.heroId === heroId && skill.slot === slot);
}

/**
 * Passive des Vanguard: Stand Your Ground (Masterprompt Abschnitt 57).
 *
 * Je mehr Gegner um ihn herum stehen, desto weniger Schaden nimmt er - UND
 * desto mehr Income bekommt er ausgezahlt. Das Maximum ist bei etwa acht
 * Gegnern erreicht; danach bringt jeder weitere nichts mehr.
 *
 * Der Income-Teil ist neu in v0.48 (Produktentscheidung vom 29.09.2026).
 *
 * Warum: Gemessen ueber 720 Matches gewannen die beiden Vanguard-Baupläne
 * ohne Income-Item 0 von 90 Matches gegen die anderen Heroes. Sein Problem war
 * nachweislich kein Schaden, sondern Geld - dieselbe Figur mit Handelssiegel
 * (Supply Line) gewann 58,3 Prozent bei sogar geringerem Schaden. Ein Tank,
 * dessen einzige Aufgabe das Halten ist, hatte keinen Weg, aus dem Halten
 * etwas zu machen. Jetzt hat er einen: Wer vorne steht, verdient mehr.
 *
 * Bewusst DIESELBE Zaehlung wie die Schadensreduktion und nicht eine zweite
 * daneben: So steuert eine Zahl beide Haelften, und das Talent "Wall" (voll
 * schon ab vier Gegnern) wirkt auf beide. Der Zuschlag je Gegner ist dadurch
 * 2,5 Prozent (mit Wall 5 Prozent) - die Obergrenze bleibt bei 20 Prozent.
 */
export const STAND_YOUR_GROUND = {
  /** Umkreis, in dem Gegner gezaehlt werden. */
  radius: 8,
  /** Ab dieser Anzahl ist die Wirkung voll. */
  enemiesForMaximum: 8,
  /** Schadensreduktion bei voller Wirkung, 0 bis 1. */
  maximumDamageReduction: 0.2,
  /**
   * Income-Zuschlag bei voller Wirkung, 0 bis 1.
   *
   * Er wirkt auf die AUSZAHLUNG, nicht auf den gespeicherten Income - genau
   * wie das Handelssiegel. Sonst wuerde der Zuschlag mit jedem Takt weiter mit
   * sich selbst wachsen.
   *
   * Gerechnet wird der DURCHSCHNITT ueber das Auszahlungsfenster von zwoelf
   * Sekunden. Wuerde nur der Augenblick der Auszahlung zaehlen, waere der
   * Zuschlag eine Lotterie: Wer zufaellig im falschen Moment zurueckweicht,
   * bekaeme nichts, und wer im richtigen Moment in der Welle steht, alles.
   */
  maximumIncomeRatio: 0.2,
} as const;

/** Obergrenze fuer die gesamte Schadensreduktion eines Heroes. */
export const MAX_TOTAL_DAMAGE_REDUCTION = 0.8;

/**
 * Mana-Regeneration des Heroes in Punkten pro Sekunde (Abschnitt 51).
 *
 * 6 -> 3 (Produktentscheidung vom 05.10.2026). Massgeblich ist der Wert je
 * Hero in heroes.ts; hier steht, was "normal" heisst.
 */
export const MANA_REGEN_PER_SECOND = 3;

/** Energy regeneriert schneller als Mana (Abschnitt 51). 12 -> 7 am 05.10.2026. */
export const ENERGY_REGEN_PER_SECOND = 7;
